import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import { getActiveEmailTask, getEmailTaskProgress } from '../services/announcementService';
import InProgressEmailCard from './InProgressEmailCard';
import styles from '../AnnouncementsV2.module.css';

const POLLING_INTERVAL = 500;

const EmailHistoryTab = ({ courseId, onTaskComplete, refreshTrigger }) => {
  const [activeTasks, setActiveTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const pollingIntervalRef = useRef(null);

  // Check for active email tasks on mount and when refreshTrigger changes
  useEffect(() => {
    checkActiveTasks();

    return () => stopPolling();
  }, [courseId, refreshTrigger]);

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const startPolling = useCallback((taskIds) => {
    stopPolling();
    if (taskIds.length === 0) return;

    pollingIntervalRef.current = setInterval(() => {
      fetchAllProgress(taskIds);
    }, POLLING_INTERVAL);
  }, [courseId]);

  const checkActiveTasks = async () => {
    setIsLoading(true);
    try {
      const response = await getActiveEmailTask(courseId);

      if (response.has_active_tasks && response.tasks.length > 0) {
        setActiveTasks(response.tasks);
        const taskIds = response.tasks.map(t => t.task_id);
        startPolling(taskIds);
      } else {
        setActiveTasks([]);
        stopPolling();
      }
    } catch (error) {
      console.error('Error checking active tasks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllProgress = async (taskIds) => {
    try {
      const progressPromises = taskIds.map(taskId =>
        getEmailTaskProgress(courseId, taskId).catch(() => null)
      );
      const results = await Promise.all(progressPromises);

      const updatedTasks = [];
      let hasCompletedTask = false;

      results.forEach((response, index) => {
        if (response) {
          if (response.status === 'COMPLETED' || response.status === 'FAILED') {
            hasCompletedTask = true;
            // Keep showing completed/failed for a moment
            updatedTasks.push({ ...response, task_id: taskIds[index] });
          } else {
            updatedTasks.push({ ...response, task_id: taskIds[index] });
          }
        }
      });

      setActiveTasks(updatedTasks);

      // If any task completed, refresh the list after a delay
      if (hasCompletedTask) {
        setTimeout(() => {
          checkActiveTasks();
          if (onTaskComplete) {
            onTaskComplete();
          }
        }, 2000);
      }

      // If all tasks are done, stop polling
      const stillActive = updatedTasks.filter(t =>
        t.status !== 'COMPLETED' && t.status !== 'FAILED'
      );
      if (stillActive.length === 0) {
        stopPolling();
      }
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  const handleRefresh = () => {
    checkActiveTasks();
  };

  // Only render when there are active tasks (hide section when no email in progress)
  if (!isLoading && activeTasks.length === 0) {
    return null;
  }

  return (
    <div className={styles.emailHistoryTab}>
      <div className={styles.tabHeader}>
        <h3>Email Sending Status</h3>
        <button
          className={styles.refreshButton}
          onClick={handleRefresh}
          disabled={isLoading}
          title="Refresh"
        >
          <FiRefreshCw size={16} />
        </button>
      </div>

      <div className={styles.emailList}>
        {isLoading ? (
          <div className={styles.loadingSpinner}>Checking for active emails...</div>
        ) : (
          activeTasks.map((task) => (
            <InProgressEmailCard
              key={task.task_id}
              progressData={task}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default EmailHistoryTab;
